// Reports (no board: design 5.5's list, in the admin boards' master brand look). Owner and admin
// only (the shell). 5 reports, each filtered by product and exported to CSV made in the browser from
// the rows already read, with British dates in UK time. The reads leave out comp and test access,
// test logins and products not in the hub, and carry no amounts (docs/data-model.md 10.4.2).
import { useMemo, useState } from 'react'
import { Link } from '../../lib/router.jsx'
import {
  PageHeader, Tabs, Card, Chips, Chip, Select, DateField, Field, Table, Button, StatusPill, Loading, Problem, InfoNote,
  useAdmin, useLoad, downloadCsv, plural, ukDate, ukDateTime, adminPath,
} from '../ui/index.js'
import {
  REPORTS, reportByKey, SHOWN, LEFT_OUT, ACCESS_STATUSES, statusWord, sourceWord, markWord, endWord, hoursMinutes, ofTotal,
  realEmail, isDeleted,
} from '../prices/reports.js'
import '../../styles/admin-prices.css'

// A person's name, linked to their page unless they've been deleted
function Who({ r }) {
  const email = realEmail(r.email)
  return (
    <>
      {isDeleted(r) || !r.person_id
        ? <span className="ad-td-main">{r.full_name || 'Deleted person'}</span>
        : <Link to={adminPath.person(r.person_id)} className="ad-td-main">{r.full_name}</Link>}
      {email && <span className="ad-td-sub">{email}</span>}
    </>
  )
}

const ProductCohort = ({ r }) => (
  <>
    <span>{r.product_name}</span>
    {r.cohort_name && <span className="ad-td-sub">{r.cohort_name}</span>}
  </>
)

const quiet = v => <span className="ad-td-quiet">{v}</span>
const num = v => <span className="rp-num">{v}</span>

// The table each report draws (the CSV has more columns: reports.js)
const TABLES = {
  progress: [
    { key: 'who', label: 'Name', width: 'minmax(0, 1.3fr)', primary: true, render: r => <Who r={r} /> },
    { key: 'product', label: 'Product', width: 'minmax(0, 1.1fr)', render: r => <ProductCohort r={r} /> },
    { key: 'status', label: 'Status', width: '150px', render: r => <><StatusPill status={r.status}>{statusWord(r.status)}</StatusPill><span className="ad-td-sub">{sourceWord(r.source)}</span></> },
    { key: 'watched', label: 'Watched', width: '100px', render: r => num(ofTotal(r.recordings_watched, r.recordings_total)) },
    { key: 'homework', label: 'Homework', width: '100px', render: r => num(r.homework_total ? ofTotal(r.homework_done, r.homework_total) : r.steps_total ? `${ofTotal(r.steps_done, r.steps_total)} steps` : 'None') },
    { key: 'last', label: 'Last watched', width: '130px', render: r => quiet(r.last_played_at ? ukDate(r.last_played_at) : 'Not yet') },
    { key: 'finished', label: 'Finished', width: '130px', render: r => quiet(r.finished_at ? `${ukDate(r.finished_at)}${r.certificate_number ? `, ${r.certificate_number}` : ''}` : 'Not yet') },
  ],
  attendance: [
    { key: 'session', label: 'Session', width: 'minmax(0, 1.1fr)', primary: true, render: r => <><span className="ad-td-main">{r.module_label && r.module_label !== r.session_title ? `${r.module_label}: ${r.session_title}` : r.session_title}</span><span className="ad-td-sub">{ukDateTime(r.starts_at)}</span></> },
    { key: 'product', label: 'Product', width: 'minmax(0, 1fr)', render: r => <ProductCohort r={r} /> },
    { key: 'who', label: 'Name', width: 'minmax(0, 1.2fr)', render: r => <Who r={r} /> },
    { key: 'mark', label: 'Register', width: '120px', render: r => <StatusPill tone={r.status === 'present' ? 'active' : r.status ? 'paused' : 'ended'}>{markWord(r.status)}</StatusPill> },
    { key: 'note', label: 'Note', width: 'minmax(0, 1fr)', render: r => (r.note ? quiet(r.note) : null) },
  ],
  'watch-time': [
    { key: 'rec', label: 'Recording', width: 'minmax(0, 1.6fr)', primary: true, render: r => <><span className="ad-td-main">{r.title}{r.part ? `, part ${r.part}` : ''}</span><span className="ad-td-sub">{[r.product_name, r.module_label].filter(Boolean).join(', ')}</span></> },
    { key: 'on', label: 'Switched on', width: '110px', render: r => quiet(r.is_published ? 'Yes' : 'No') },
    { key: 'length', label: 'Length', width: '90px', render: r => num(r.duration_seconds ? hoursMinutes(r.duration_seconds) : 'Not set') },
    { key: 'viewers', label: 'Started it', width: '100px', render: r => num(r.viewers ?? 0) },
    { key: 'watched', label: 'Watched it', width: '100px', render: r => num(r.watched ?? 0) },
    { key: 'time', label: 'Watch time', width: '110px', render: r => num(hoursMinutes(r.seconds_watched)) },
  ],
  logins: [
    { key: 'who', label: 'Name', width: 'minmax(0, 1.3fr)', primary: true, render: r => <Who r={r} /> },
    { key: 'products', label: 'Products', width: 'minmax(0, 1.4fr)', render: r => quiet(r.products || '') },
    { key: 'logins', label: 'Logins', width: '90px', render: r => num(r.logins ?? 0) },
    { key: 'how', label: 'Code, password', width: '130px', render: r => num(`${r.code_logins ?? 0}, ${r.password_logins ?? 0}`) },
    { key: 'last', label: 'Last login', width: '150px', render: r => quiet(r.last_login_at ? ukDateTime(r.last_login_at) : 'Never') },
  ],
  access: [
    { key: 'who', label: 'Name', width: 'minmax(0, 1.3fr)', primary: true, render: r => <Who r={r} /> },
    { key: 'product', label: 'Product', width: 'minmax(0, 1.1fr)', render: r => <ProductCohort r={r} /> },
    { key: 'status', label: 'Status', width: '150px', render: r => <><StatusPill status={r.status}>{statusWord(r.status)}</StatusPill><span className="ad-td-sub">{sourceWord(r.source)}</span></> },
    { key: 'started', label: 'Started', width: '110px', render: r => quiet(ukDate(r.starts_at)) },
    {
      key: 'dates', label: 'Dates', width: 'minmax(0, 1.2fr)',
      render: r => quiet([
        r.ended_at ? `Ended ${ukDate(r.ended_at)}${r.end_reason ? `, ${endWord(r.end_reason).toLowerCase()}` : ''}` : r.ends_at ? `Ends ${ukDate(r.ends_at)}` : 'No end date',
        r.balance_due_at ? `Balance due ${ukDate(r.balance_due_at)}` : null,
        r.suspend_on ? `Suspends on ${ukDate(r.suspend_on)}` : null,
        r.paused_until ? `Paused until ${ukDate(r.paused_until)}` : null,
      ].filter(Boolean).join('. ')),
    },
  ],
}

export default function Reports({ route }) {
  const { api, products, setFlash } = useAdmin()
  const report = reportByKey(route.params.report) || REPORTS[0]
  const [filters, setFilters] = useState({ product: '', cohort: '', from: '', to: '', status: '' })
  const set = patch => setFilters(f => ({ ...f, ...patch }))

  // the paid products in the hub; a 1-2-1 space has no report of its own
  const choices = useMemo(() => (products || [])
    .filter(p => p.access_kind === 'paid' && p.moved_in && p.type !== 'client_space')
    .sort((a, b) => (a.sort_order ?? 99) - (b.sort_order ?? 99)), [products])
  const product = choices.find(p => p.id === filters.product) || null

  const cohorts = useLoad(async () => (report.filters.includes('cohort') && filters.product
    ? api.run(api.from('cohorts').select('id, product_id, name, starts_on').eq('product_id', filters.product).order('starts_on'))
    : []), [report.key, filters.product])

  const args = report.args(filters)
  // the dates go to the read as London days: it counts sessions held, or logins, in the range
  const rows = useLoad(() => api[report.read](args), [report.key, JSON.stringify(args)])

  const list = rows.data || []
  const datesWrong = filters.from && filters.to && filters.from > filters.to

  function exportCsv() {
    const file = downloadCsv(`${report.file}${product ? `-${product.slug}` : ''}`, report.csv, list)
    setFlash({ text: `CSV downloaded: ${file}` })
  }

  return (
    <>
      <PageHeader eyebrow="From the hub's own records" title="Reports"
        actions={<Button kind="ink" onClick={exportCsv} disabled={rows.status !== 'ready' || list.length === 0}>Export CSV</Button>} />
      <Tabs label="Reports" current={report.key}
        items={REPORTS.map(r => ({ key: r.key, label: r.label, to: adminPath.report(r.key) }))} />

      <Card title={report.label} aside={report.line}>
        <div className="rp-filters">
          <Chips label="Product">
            <Chip pressed={!filters.product} onClick={() => set({ product: '', cohort: '' })}>All products</Chip>
            {choices.map(p => (
              <Chip key={p.id} product={p} pressed={filters.product === p.id} onClick={() => set({ product: p.id, cohort: '' })}>{p.name}</Chip>
            ))}
          </Chips>
          {report.filters.includes('status') && (
            <Chips label="Status">
              <Chip pressed={!filters.status} onClick={() => set({ status: '' })}>Every status</Chip>
              {ACCESS_STATUSES.map(s => <Chip key={s} pressed={filters.status === s} onClick={() => set({ status: s })}>{statusWord(s)}</Chip>)}
            </Chips>
          )}
          {(report.filters.includes('dates') || (report.filters.includes('cohort') && product)) && (
            <div className="rp-filter-row">
              {report.filters.includes('cohort') && product && (
                <Field label="Cohort">
                  {props => (
                    <Select {...props} value={filters.cohort} onChange={cohort => set({ cohort })} placeholder="Every cohort"
                      options={(cohorts.data || []).map(c => ({ value: c.id, label: c.name }))} />
                  )}
                </Field>
              )}
              {report.filters.includes('dates') && (
                <>
                  <DateField label="From" value={filters.from} onChange={from => set({ from })} max={filters.to || undefined} />
                  <DateField label="To" value={filters.to} onChange={to => set({ to })} min={filters.from || undefined}
                    error={datesWrong ? 'Pick a date on or after the From date.' : undefined} />
                  {(filters.from || filters.to) && <Button size="sm" onClick={() => set({ from: '', to: '' })}>Clear the dates</Button>}
                </>
              )}
            </div>
          )}
          <p className="rp-left-out">{LEFT_OUT}</p>
        </div>
      </Card>

      {rows.status === 'loading' && <Loading />}
      {rows.status === 'error' && <Problem error={rows.error} onRetry={rows.reload} />}
      {rows.status === 'ready' && (
        <>
          <p className="rp-count" role="status">
            <strong>{plural(list.length, report.noun[0], report.noun[1])}</strong>
            {list.length > SHOWN ? `. Showing the first ${SHOWN}. The CSV has every row.` : ''}
          </p>
          <Table label={report.label} className="rp-table" columns={TABLES[report.key]} rows={list.slice(0, SHOWN)}
            rowKey={(r, i) => `${r.person_id || r.recording_id || ''}:${r.session_id || r.access_id || r.product_id || ''}:${r.cohort_id || ''}:${i}`}
            empty="Nothing to show for these filters." />
          {report.key === 'attendance' && <InfoNote>Each session's register is taken under it, in Cohorts and dates.</InfoNote>}
        </>
      )}
    </>
  )
}
